"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Button, TextInput, Group, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { useOpenContactMethodModals } from "@/lib/utils/crm/use-open-contact-method-modals";

// Types
import type { DetailActionProps, BaseEntity } from "../types/detailPage";

// ============================================================================
// CALL ACTION COMPONENT
// ============================================================================

interface CallActionProps<T extends BaseEntity> extends DetailActionProps<T> {
  /** Phone field name */
  phoneField?: string;

  /** Name field name for display */
  nameField?: string;
}

/**
 * Reusable Call Action Component
 * Can be used for any entity with a phone number (contacts, companies, etc.)
 */
export function CallAction<T extends BaseEntity>({
  action,
  item,
  context,
  loading = false,
  phoneField = "phone",
  nameField = "name",
}: CallActionProps<T>) {
  const [opened, { open, close }] = useDisclosure(false);
  const [phoneInput, setPhoneInput] = useState((item as any)[phoneField] || "");
  const { openText } = useOpenContactMethodModals();

  // Get current phone and name values
  const currentPhone = (item as any)[phoneField] || "";
  const itemName = (item as any)[nameField] || "this contact";
  const entityType = context.entityName;

  const contactContext = useMemo(
    () => ({
      item: item as Record<string, unknown>,
      entityType,
    }),
    [entityType, item]
  );

  const openTextForPhone = useCallback(
    (phone: string) => {
      openText(phone, contactContext);
    },
    [contactContext, openText]
  );

  // Handle phone call — opens send-text modal (Quo call inside)
  const handleCall = useCallback(() => {
    if (currentPhone) {
      openTextForPhone(String(currentPhone));
    } else {
      setPhoneInput("");
      open();
    }
  }, [currentPhone, open, openTextForPhone]);

  // Handle phone number save and open text modal
  const handleSaveAndCall = useCallback(async () => {
    const phoneValue = phoneInput.trim();

    if (!phoneValue) return;

    try {
      await context.onUpdateItem({
        [phoneField]: phoneValue,
      } as Partial<T>);

      close();
      void context.onRefresh();
      openTextForPhone(phoneValue);
      context.onToast("Phone number saved — opening text composer…", "success");
    } catch (error) {
      console.error("Failed to save phone number:", error);
      context.onToast("Failed to save phone number", "error");
    }
  }, [phoneInput, phoneField, context, close, openTextForPhone]);

  return (
    <>
      {/* Call Button */}
      <Button variant="outline" size="sm" onClick={handleCall} disabled={loading}>
        {currentPhone ? `Call ${currentPhone}` : "Add Phone"}
      </Button>

      {/* Phone Prompt Modal */}
      <Modal opened={opened} onClose={close} title="Add Phone Number">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            We need a phone number for {itemName} to place a call.
          </p>

          <TextInput
            label="Phone Number"
            placeholder="Enter phone number"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            required
          />

          <Group justify="flex-end">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleSaveAndCall} disabled={!phoneInput.trim()}>
              Save & Call
            </Button>
          </Group>
        </div>
      </Modal>
    </>
  );
}

// Export with generic type
export { CallAction as CallActionComponent };
